<template>
    <div
        class="list"
        :class="{ embedded: isEmbedded }"
        @dragover.prevent="$emit('dragover-group', 'pinned')"
        @dragleave="$emit('dragleave-group', 'pinned')"
        @drop.prevent="$emit('drop-group', 'pinned')"
    >
        <router-link to="/library" class="item special">
            <i class="far fa-plus"></i>
            Start a Form
        </router-link>

        <router-link class="title" to="/library">Form Library</router-link>
        <div class="group">
            <router-link to="/library" class="item">
                <i class="far fa-books"></i>
                All Forms
            </router-link>
            <router-link to="/boxes/0/public" class="item" v-if="user.hasRole('editor')">
                <i class="far fa-link"></i>
                Public Links
            </router-link>
        </div>
        <div
            class="form-group group pinned"
            :class="{
                empty: !pinnedItems.length,
                highlight: highlightGroup === 'pinned',
            }"
        >
            <div v-for="(item, index) in pinnedItems" :key="index">
                <router-link v-if="item.path" :to="item.path" class="item" :class="item.class">
                    <i :class="item.icon" v-show="item.icon"></i>
                    {{ item.name }}
                    <span
                        class="far fa-thumbtack unpin"
                        title="Unpin folder"
                        @click.prevent.stop="$store.dispatch('library/unpinPath', item.realPath)"
                    ></span>
                </router-link>
                <div
                    v-else
                    class="item"
                    @click="$emit('click', item.id)"
                    @dblclick="$emit('dblclick', item.id)"
                    :class="[item.class, { active: activeId === item.id }]"
                >
                    <i :class="item.icon" v-show="item.icon"></i>
                    {{ item.name }}
                    <span
                        class="far fa-thumbtack unpin"
                        title="Unpin form"
                        @click.prevent.stop="$store.dispatch('library/unpinPath', item.realPath)"
                    ></span>
                </div>
            </div>
        </div>

        <div v-for="(currentBox, availableBoxesIndex) in availableBoxes" :key="availableBoxesIndex">
            <div class="title" v-if="availableBoxesIndex === 0 && !isSuperAdmin">My Boxes</div>
            <div
                class="title"
                v-if="(availableBoxesIndex === 1 && !isSuperAdmin) || (availableBoxesIndex === 0 && isSuperAdmin)"
            >Shared Boxes</div>
            <div
                class="group box-group"
                :class="{
                    'route-match':
                        $route.params.hasOwnProperty('boxIndex') &&
                        parseInt($route.params.boxIndex) === availableBoxesIndex &&
                        $route.params.filter !== 'public',
                    active: openBoxes.includes(currentBox.info.id),
                }"
            >
                <router-link :to="`/boxes/${availableBoxesIndex}/inbox`" class="item box-name">
                    <div class="name">
                        {{ currentBox.info.name }}
                    </div>
                    <div class="rs">
                        <div class="count" v-if="boxCounts[currentBox.info.id] && boxCounts[currentBox.info.id].Inbox">
                            {{ boxCounts[currentBox.info.id].Inbox }}
                        </div>
                        <div class="toggle" @click.prevent.stop="toggleBoxOpen(currentBox.info.id)">
                            <i class="far fa-chevron-down"></i>
                        </div>
                    </div>
                </router-link>
                <router-link
                    :to="`/boxes/${availableBoxesIndex}/${box.path}`"
                    class="item boxes"
                    v-for="(box, boxIndex) in currentBox.folders.filter((box) => box.visible)"
                    :key="boxIndex"
                >
                    <div>
                        <i :class="box.icon" v-show="box.icon"></i>
                        {{ box.name }}
                    </div>
                    <div class="count" v-if="boxCounts[currentBox.info.id] && boxCounts[currentBox.info.id][box.name]">
                        {{ boxCounts[currentBox.info.id][box.name] }}
                    </div>
                </router-link>
            </div>
        </div>

        <div class="title">Data Elements</div>
        <div class="group">
            <router-link
                :to="`/dataElementsList/${def.id}`"
                class="item"
                v-for="(def, index) in dataElementDefinitions"
                :key="index"
            >
                <i :class="`far fa-${def.icon}`" v-show="def.icon"></i>
                {{ def.name_plural }}
            </router-link>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { constants } from '../utils';

    export default {
        name: 'LeftNav',
        data() {
            return {
                openBoxes: [],
            };
        },
        props: {
            title: {
                type: String,
            },
            activeId: {
                String,
                default: null,
            },
            highlightGroup: {
                type: String,
                default: '',
            },
        },
        computed: {
            ...mapGetters({
                user: 'user/user',
                paths: 'library/paths',
                forms: 'library/forms',
                endOfPath: 'library/endOfPath',
                availableBoxes: 'boxes/availableBoxes',
                pathName: 'library/pathName',
                boxCounts: 'boxes/counts',
                isEmbedded: 'base/isEmbedded',
                isSuperAdmin: 'auth/isSuperAdmin',
            }),
            pinned() {
                return this.user.pinned_paths;
            },
            pinnedItems() {
                return this.pinned
                    .filter((path) => this.paths[path])
                    .map((path) => {
                        const item = { name: this.pathName(path) };
                        const pathObj = this.paths[path];
                        if (!pathObj.form_template_id) {
                            item.icon = 'far fa-folder-open';
                            item.path = '/library/folder' + path;
                            item.realPath = path;
                        } else {
                            item.realPath = path;
                            item.id = path;
                            if (this.forms[pathObj.form_template_id].is_draft) {
                                item.icon = constants.ICONS.FORM_DRAFT;
                            } else {
                                item.icon = constants.ICONS.FORM;
                            }
                        }
                        return item;
                    });
            },
            dataElementDefinitions() {
                return [...this.$store.getters['dataElements/getDefinitions']].sort((a, b) => {
                    return a.name > b.name ? 1 : -1;
                });
            },
        },
        methods: {
            toggleBoxOpen(id) {
                let index = this.openBoxes.indexOf(id);
                if (index === -1) {
                    this.openBoxes.push(id);
                } else {
                    this.openBoxes.splice(index, 1);
                }

                localStorage.openBoxes = JSON.stringify(this.openBoxes);
            },
        },
        async mounted() {
            this.$store.commit('layout/widthLeftBar', 250);

            // after DOM updates, make some non-essential fetches
            await this.$nextTick();

            // if library has never been loaded, load it (necessary for pinned items)
            if (this.$route.name !== 'FormLibrary' && !Object.keys(this.forms).length && !Object.keys(this.paths).length) {
                this.$store.dispatch('library/load');
            }

            if (localStorage.openBoxes) {
                this.openBoxes = JSON.parse(localStorage.openBoxes);
            } else {
                this.openBoxes.push(this.user.id);
            }

            this.$store.dispatch('boxes/loadCounts');
        },
    };
</script>

<style lang="scss" scoped>
    $color-route-match: darken($color-main, 3);

    .list {
        display: flex;
        flex-direction: column;
        width: 250px;
        flex-shrink: 0;
        height: 100%;
        max-width: 40%;
        overflow: auto;
        background: $color-main;

        * {
            user-select: none;
        }

        @include nav-scrollbar;

        // Style overrides for embedded mode
        &.embedded {
            $color-black-alpha: rgba($color-black, 0.75);
            $color-route-match-embed: darken($color-disabled-bg, 8);
            background: $color-disabled-bg;
            scrollbar-color: $color-black-alpha $color-disabled-bg;

            &::-webkit-scrollbar-track {
                background: $color-disabled-bg;
            }

            &::-webkit-scrollbar-thumb {
                background-color: $color-black-alpha;
                border-radius: 6px;
                border: 3px solid $color-disabled-bg;
            }

            .title {
                color: $color-black-alpha;
            }

            .item {
                color: $color-black;

                &:hover:not(.router-link-active):not(.active) {
                    background: $color-route-match-embed;
                }

                &.router-link-exact-active,
                &.active {
                    background: $color-route-match-embed;
                }

                &.special {
                    margin-top: 1.5rem;

                    &.router-link-exact-active,
                    &.active {
                        background: $color-disabled-bg;
                    }
                }

                &.boxes {
                    &::before {
                        color: $color-black;
                    }

                    .count {
                        color: $color-black-alpha;
                    }
                }
            }

            .box-group {
                background-color: darken($color-disabled-bg, 2);
                
                &.route-match:not(.active) .box-name {
                    background: $color-route-match-embed;
                }

                .rs {
                    color: $color-black-alpha;
                }

                .toggle:hover {
                    background-color: darken($color-route-match-embed, 4);
                }

                &.active .box-name:not(:hover) {
                    background-color: $color-disabled-bg;
                }

                .box-name {
                    background-color: $color-disabled-bg;
                }
            }
        }
    }

    .title {
        font-weight: $bold;
        color: $color-white-alpha;
        text-transform: uppercase;
        font-size: 0.7rem;
        text-align: left;
        margin-left: 1rem;
        margin-top: 2em;
    }

    .item {
        text-decoration: none;
        text-align: left;
        height: 2rem;
        line-height: 2rem;
        display: block;
    }

    .form-group {
        position: relative;

        &:after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0.5em;
            right: 0.5em;
            pointer-events: none;
            border: 1px dashed transparent;
            transition: border-color 0.2s;
        }

        &:not(.empty).highlight:after {
            border-color: $color-almost-white;
        }

        .no-items {
            display: none;
            margin: 0.5rem 1rem 0 1rem;
            opacity: 0.5;
            border: 0.5px dashed rgba(255, 255, 255, 0.3);
            padding: 0.5rem 1.5rem;
            transition: opacity 0.2s, border-color 0.2s;
            font-size: 0.85em;
        }

        &.empty .no-items {
            display: block;
        }

        &.highlight .no-items {
            opacity: 1;
            border-color: $color-white;
        }
    }

    .item {
        cursor: pointer;
        text-align: left;
        color: $color-white;
        white-space: nowrap;
        padding: 0 1.5rem 0 1.1rem;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 500;
        user-select: none;
        position: relative;

        &:hover:not(.router-link-active):not(.active) {
            background: $color-route-match;
        }

        &.router-link-exact-active,
        &.active {
            background: $color-route-match;
        }

        &.special {
            margin-top: 1.5rem;
            flex-shrink: 0;

            &.router-link-exact-active,
            &.active {
                background: $color-main;
            }
        }

        i {
            margin-right: 0.5em;
            text-align: center;
            width: 1rem;
        }

        .unpin {
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
            right: 10px;
            opacity: 0;
            cursor: pointer;
            transition: opacity 0.2s;
            padding: 5px;
        }

        &:hover .unpin {
            opacity: 0.7;
        }
    }

    .item.boxes {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding-right: 0.75rem;

        &::before {
            content: "‣";
            position: absolute;
            left: 18px;
            color: $color-white;
            opacity: 0.3;
        }

        .count {
            line-height: 1em;
            display: flex;
            
            align-items: center;
            justify-content: center;
            padding: 0.25em;
            color: $color-white-alpha;
            font-size: $font-size-smaller;
        }
    }

    .dragging-over {
        @include dragging-over;
    }

    .box-group {
        background-color: darken($color-main, 1);

        .box-name {
            display: flex;
            justify-content: space-between;
            padding-right: 0;
            background-color: $color-main;

            .name {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }
        }

        &.route-match:not(.active) .box-name {
            background: $color-route-match;
        }

        .rs {
            color: $color-white-alpha;
            display: flex;
        }

        .toggle {
            margin-left: 0.5em;
            padding: 0 0.75em;
            transition: 0.2s background-color;
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover {
                background-color: darken($color-main, 4);
            }

            i {
                margin: 0;
                transition: 0.3s transform;
            }
        }

        &.active {
            .box-name {
                &:not(:hover) {
                    background-color: $color-main;
                }

                .count {
                    display: none;
                }
            }

            .toggle i {
                transform: rotate(180deg);
            }
        }

        .boxes {
            padding-left: 2.5em;

            &:nth-child(2) {
                box-shadow: inset 0px 5px 5px -5px rgba(0, 0, 0, 0.1);
            }

            &:last-child {
                box-shadow: inset 0px -5px 5px -5px rgba(0, 0, 0, 0.1);
            }
        }

        &:not(.active) {
            .boxes {
                display: none;
            }
        }
    }
</style>
